/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";
import {
  CategoryProductTree,
  type CategoryTreeGroup,
} from "@app/vue/components/CategoryProductTree";

type CategoryProduct = {
  product_id: number;
  product_name: string;
  sales: number;
  discounts: number;
  promotions: number;
  net_sales: number;
};

type ProductCategory = {
  category_id: number;
  category_name: string;
  color: string | null;
  sales: number;
  discounts: number;
  promotions: number;
  net_sales: number;
  product_count: number;
  products: CategoryProduct[];
};

const TREE_COLUMNS = [
  { key: "sales", labelKey: "reports.coa.sales", format: "money" as const },
  {
    key: "discounts",
    labelKey: "reports.coa.rev_discounts",
    format: "money" as const,
  },
  {
    key: "promotions",
    labelKey: "reports.coa.rev_promotions",
    format: "money" as const,
  },
  {
    key: "net_sales",
    labelKey: "reports.coa.net_sales",
    format: "money" as const,
  },
];

export const CategoriesTab = defineComponent<{ from: string; to: string }>({
  name: "ChartCategoriesTab",
  props: ["from", "to"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = computed(() => auth.user.value?.restaurant.currency || "CHF");
    const collapsed = ref(new Set<number>());

    const categoriesQuery = useQuery({
      queryKey: computed(() => [
        "accounting",
        "products-by-category",
        props.from,
        props.to,
      ]),
      queryFn: () =>
        trpc.accounting.productsByCategory.query({
          from: props.from,
          to: props.to,
        }),
    });

    const categories = computed<ProductCategory[]>(
      () => categoriesQuery.data.value ?? [],
    );

    const treeCategories = computed<CategoryTreeGroup[]>(() =>
      categories.value.map((category) => ({
        id: category.category_id,
        name: category.category_name,
        color: category.color,
        values: {
          sales: category.sales,
          discounts: category.discounts,
          promotions: category.promotions,
          net_sales: category.net_sales,
        },
        products: category.products.map((product) => ({
          id: product.product_id,
          name: product.product_name,
          values: {
            sales: product.sales,
            discounts: product.discounts,
            promotions: product.promotions,
            net_sales: product.net_sales,
          },
        })),
      })),
    );

    const toggleCategory = (categoryId: number | string) => {
      const next = new Set(collapsed.value);
      next.has(categoryId as number)
        ? next.delete(categoryId as number)
        : next.add(categoryId as number);
      collapsed.value = next;
    };

    return () => (
      <div class="h-full overflow-auto">
        <CategoryProductTree
          categories={treeCategories.value}
          columns={TREE_COLUMNS.map((c) => ({
            key: c.key,
            format: c.format,
            label: t(c.labelKey),
          }))}
          currency={currency.value}
          collapsed={collapsed.value}
          onToggleCategory={toggleCategory}
          loading={categoriesQuery.isLoading.value}
          error={categoriesQuery.isError.value}
          emptyMessage={t("reports.coa.empty_categories")}
          errorMessage={t("reports.coa.error_load")}
          retryLabel={t("reports.coa.retry")}
          onRetry={() => categoriesQuery.refetch()}
          productColumnLabel={t("reports.coa.col_product")}
          categoryTotalLabel={t("reports.coa.category_total")}
          expandLabel={(name) => t("reports.coa.expand_category", [name])}
          collapseLabel={(name) => t("reports.coa.collapse_category", [name])}
        />
      </div>
    );
  },
});
